// ============================================================
// Sonar renderer — 200×200 を 200×100 ×2 に分割した PNG として出力
// ============================================================
// SDK の image container 上限は 200×100 なので、200×200 のソナーを描き、
// 上半分 / 下半分の 2 枚の PNG に分割して送る (v2 stacked sonar)。
// SDK は受信した PNG を 4-bit greyscale に変換して glasses に表示する。

import type { NearbyState, SpotlightItem } from '../types'
import { distanceMeters, metersToSonarPx, toRad } from '../util/distance'

export interface StackedFrame {
  /** 上半 200×100 PNG */
  top: Uint8Array
  /** 下半 200×100 PNG */
  bottom: Uint8Array
}

/** greyscale level 0–15 を Canvas2D の rgba 値に変換 (G2 は緑単色) */
function gray(v: number): string {
  const g = Math.round(Math.min(15, Math.max(0, v)) * (255 / 15))
  return `rgba(0, ${g}, 0, 1)`
}

const W = 200
const H_SRC = 200    // 1 枚の論理キャンバス高さ
const H_HALF = 100   // image container 1 つあたりの高さ
const cx = 100
const cy = 100       // 上下半分の境界に乗せる (連続性のため少し上に寄せても可)

export class SonarRenderer {
  private src: OffscreenCanvas | null = null
  private top: OffscreenCanvas | null = null
  private bot: OffscreenCanvas | null = null
  private last: StackedFrame | null = null

  /** 1 フレーム描画 → 上下 2 枚の PNG bytes */
  async render(state: NearbyState): Promise<StackedFrame> {
    const g = this.getSrcCtx()
    // 背景クリア
    g.fillStyle = gray(0)
    g.fillRect(0, 0, W, H_SRC)

    // 最外周を canvas 半径 -2 (1cell の余白) に揃える
    const outerPx = 96
    const scale = outerPx / state.radiusM

    // リング (3 本)
    drawRing(g, cx, cy, 32, gray(11))    // 内 (radius/3)
    drawRing(g, cx, cy, 64, gray(11))    // 中
    drawRing(g, cx, cy, 96, gray(13))    // 外

    // 自分 (中心の十字)
    g.fillStyle = gray(15)
    g.fillRect(cx - 4, cy, 9, 1)
    g.fillRect(cx, cy - 4, 1, 9)

    // 北 tick (image 上端)
    g.fillStyle = gray(15)
    g.fillRect(cx, 0, 1, 6)

    // エンティティ描画 — 種別ごとに明確に違う形状で
    //   駅 (■):   ホロウ角丸スクエア 6×6  level 12 — landmark, 中心が空いてるので積層に強い
    //   バス停 (・): 1px ドット             level 7  — minimal, context のみ
    //   バス (●):   塗り円 3px             level 15 — moving, 最も目立つ
    //   電車 (▲):   塗り三角 5px            level 15 — moving, 別形状で区別
    for (const e of state.nearby) {
      const fix = state.fix
      const dy = (e.lat - fix.lat) * 111_320
      const dx = (e.lon - fix.lon) * 111_320 * Math.cos(toRad(fix.lat))
      const { x, y } = metersToSonarPx(dx, dy, fix.heading, scale, cx, cy)
      if (x < 0 || x >= W || y < 0 || y >= H_SRC) continue

      switch (e.kind) {
        case 'station':
          drawHollowRect(g, Math.round(x), Math.round(y), 3, gray(12))
          break
        case 'busstop':
          // 2×2 ドット (1px だと視認不可)
          g.fillStyle = gray(8)
          g.fillRect(Math.round(x) - 1, Math.round(y), 2, 1)
          g.fillRect(Math.round(x) - 1, Math.round(y) - 1, 2, 1)
          break
        case 'train':
          drawTriangleUp(g, x, y, 4, gray(15))
          break
        case 'bus':
          drawDisc(g, x, y, 3, gray(e.moving ? 15 : 9))
          break
        case 'self':
          // skip — already drawn
          break
      }
    }

    // スポットライト中の対象に halo (二重円) + 小さなラベル
    drawSpotlightHaloWithLabel(g, state.fix.lat, state.fix.lon, state.fix.heading, scale, state.spotlightBus, gray(15))
    drawSpotlightHaloWithLabel(g, state.fix.lat, state.fix.lon, state.fix.heading, scale, state.spotlightTrain, gray(15))

    // 最寄駅 2 件に駅名ラベル
    const stationsNear = state.nearby
      .filter((e) => e.kind === 'station')
      .map((e) => ({ e, d: distanceMeters(state.fix, e) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 2)
    for (const { e } of stationsNear) {
      const dy = (e.lat - state.fix.lat) * 111_320
      const dx = (e.lon - state.fix.lon) * 111_320 * Math.cos(toRad(state.fix.lat))
      const { x, y } = metersToSonarPx(dx, dy, state.fix.heading, scale, cx, cy)
      if (x < 0 || x >= W || y < 0 || y >= H_SRC) continue
      const name = e.label?.replace(/駅$/, '') ?? ''
      if (name) drawText(g, name, x + 5, y + 1, 9, gray(13))
    }

    // 画像下端に小さな凡例 (LVGL 固定フォントを回避して 9px で)
    drawText(g, '● バス  □ 駅  ▲ 電車  ・ 停', 4, H_SRC - 6, 9, gray(13))

    // 上下に分割 → 2 PNG
    const topCanvas = this.getTopCanvas()
    const botCanvas = this.getBotCanvas()
    const tg = topCanvas.getContext('2d')!
    const bg = botCanvas.getContext('2d')!
    tg.clearRect(0, 0, W, H_HALF)
    bg.clearRect(0, 0, W, H_HALF)
    // 上半分: 元の (0, 0, W, H_HALF) → top の (0, 0)
    tg.drawImage(this.src!, 0, 0, W, H_HALF, 0, 0, W, H_HALF)
    // 下半分: 元の (0, H_HALF, W, H_HALF) → bottom の (0, 0)
    bg.drawImage(this.src!, 0, H_HALF, W, H_HALF, 0, 0, W, H_HALF)

    const [topBlob, botBlob] = await Promise.all([
      topCanvas.convertToBlob({ type: 'image/png' }),
      botCanvas.convertToBlob({ type: 'image/png' }),
    ])
    const top = new Uint8Array(await topBlob.arrayBuffer())
    const bottom = new Uint8Array(await botBlob.arrayBuffer())

    const frame: StackedFrame = { top, bottom }
    this.last = frame
    return frame
  }

  lastFrame(): StackedFrame | null {
    return this.last
  }

  // ───────── private ─────────

  private getSrcCtx(): OffscreenCanvasRenderingContext2D {
    if (!this.src) {
      if (typeof OffscreenCanvas === 'undefined') {
        throw new Error('OffscreenCanvas is not supported in this environment')
      }
      this.src = new OffscreenCanvas(W, H_SRC)
    }
    return this.src.getContext('2d', { willReadFrequently: true })!
  }

  private getTopCanvas(): OffscreenCanvas {
    if (!this.top) this.top = new OffscreenCanvas(W, H_HALF)
    return this.top
  }

  private getBotCanvas(): OffscreenCanvas {
    if (!this.bot) this.bot = new OffscreenCanvas(W, H_HALF)
    return this.bot
  }
}

/** stroke() が一部環境で空 paint になることがあるので fillRect ベースで描画 */
function drawRing(
  g: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, radius: number, fillStyle: string,
): void {
  g.fillStyle = fillStyle
  // 8 等分の中点法を簡易化: angle で 360 点回す (200×100 のサイズなら速い)
  const steps = Math.max(48, Math.ceil(radius * Math.PI * 2))
  for (let i = 0; i < steps; i++) {
    const t = (i / steps) * Math.PI * 2
    const x = Math.round(cx + radius * Math.cos(t))
    const y = Math.round(cy + radius * Math.sin(t))
    g.fillRect(x, y, 1, 1)
  }
}

/** arc() + fill() の代わりに fillRect で塗り潰し円を描く */
function drawDisc(
  g: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, radius: number, fillStyle: string,
): void {
  g.fillStyle = fillStyle
  const r = Math.max(1, Math.ceil(radius))
  const r2 = radius * radius
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy <= r2) {
        g.fillRect(Math.round(cx + dx), Math.round(cy + dy), 1, 1)
      }
    }
  }
}

/** バス停の小さなダイヤモンド (現在未使用、参考) */
function drawDiamond(
  g: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, size: number,
): void {
  for (let dy = -size; dy <= size; dy++) {
    const w = size - Math.abs(dy)
    g.fillRect(cx - w, cy + dy, w * 2 + 1, 1)
  }
}

/** 駅: ホロウ (枠だけ) 角丸スクエア — 中央が空く landmark シンボル */
function drawHollowRect(
  g: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, size: number, fillStyle: string,
): void {
  g.fillStyle = fillStyle
  const s = size
  // 上下辺
  g.fillRect(cx - s + 1, cy - s, (s - 1) * 2 + 1, 1)
  g.fillRect(cx - s + 1, cy + s, (s - 1) * 2 + 1, 1)
  // 左右辺
  g.fillRect(cx - s, cy - s + 1, 1, (s - 1) * 2 + 1)
  g.fillRect(cx + s, cy - s + 1, 1, (s - 1) * 2 + 1)
  // 角を少し落として角丸に
  g.fillRect(cx - s + 1, cy - s, 1, 1)
  g.fillRect(cx + s - 1, cy - s, 1, 1)
  g.fillRect(cx - s + 1, cy + s, 1, 1)
  g.fillRect(cx + s - 1, cy + s, 1, 1)
}

/** 電車: 塗り上向き三角 — 進行方向の手がかりにも */
function drawTriangleUp(
  g: OffscreenCanvasRenderingContext2D,
  cx: number, cy: number, size: number, fillStyle: string,
): void {
  g.fillStyle = fillStyle
  // 頂点 cy-size、底辺 cy+size、底辺幅 2*size+1
  for (let dy = 0; dy <= size * 2; dy++) {
    const half = Math.round((dy / (size * 2)) * size)
    g.fillRect(Math.round(cx) - half, Math.round(cy) - size + dy, half * 2 + 1, 1)
  }
}

/** スポットライト halo + ラベル */
function drawSpotlightHaloWithLabel(
  g: OffscreenCanvasRenderingContext2D,
  selfLat: number, selfLon: number,
  heading: number | null,
  scale: number,
  item: SpotlightItem | null,
  fillStyle: string,
): void {
  if (!item) return
  const θ = (item.bearing - (heading ?? 0)) * Math.PI / 180
  const r = item.distanceM * scale
  const x = 100 + r * Math.sin(θ)
  const y = 100 - r * Math.cos(θ)
  if (x < 0 || x >= 200 || y < 0 || y >= 200) return
  drawRing(g, x, y, 6, fillStyle)
  drawRing(g, x, y, 8, fillStyle)

  // 小さなラベル: 系統名のみ (距離は左カラムにあるので冗長を避ける)
  const lineShort = item.line.replace(/^都営バス\s*/, '').replace(/\s.*$/, '')
  const text = item.mins < 90
    ? `${lineShort} ${item.mins}分`
    : lineShort
  drawText(g, text, x + 10, y - 4, 10, gray(15))
}

/** Canvas2D で小さな文字を描画 (LVGL 固定フォント回避) */
function drawText(
  g: OffscreenCanvasRenderingContext2D,
  text: string, x: number, y: number, sizePx: number, fillStyle: string,
): void {
  g.fillStyle = fillStyle
  g.font = `${sizePx}px "Hiragino Sans", "Yu Gothic", "Noto Sans CJK JP", sans-serif`
  g.textBaseline = 'middle'
  g.textAlign = 'left'
  g.fillText(text, x, y)
}

